import { useEffect, useRef, useState } from 'react';
import type { AnnPoint, Annotation, AnnotationPatch, NewAnnotation } from '../../annotation/types';
import { pointsEqual } from '../../annotation/geometry';
import { counterColorIndex } from '../../annotation/style';
import type { CounterSession } from './AnnotationOverlay';

// How long a count rests before its dots are saved. The first dot is saved at
// once, so the group is on the server, with its id, almost from the start.
export const COUNTER_SAVE_DELAY_MS = 600;

/** The writes a count makes: `create` resolves to the saved record, or to
 *  nothing when the server refused it (the refusal is reported elsewhere). */
export interface CounterWrites {
  create: (a: NewAnnotation) => Promise<Annotation | undefined>;
  update: (id: string, patch: AnnotationPatch) => void;
  remove: (id: string) => void;
}

// One group being counted, and what the server holds of it.
interface Group {
  id: string | null;
  study: string;
  series: string | null;
  frameOfReferenceUID: string | null;
  colorIndex: number;
  points: AnnPoint[];
  /** The points last written, or null before the first save. */
  sent: AnnPoint[] | null;
  /** The create on its way, resolving to the saved id (null if refused). */
  creating: Promise<string | null> | null;
  /** Dropped: nothing more is written for it. */
  discarded: boolean;
}

/**
 * A counting session that saves as it goes: the first dot creates the counter,
 * later dots are written once the count rests for COUNTER_SAVE_DELAY_MS. Ending
 * a group is then never what keeps it, and a group always saves to the slide
 * it was counted on, whatever is on screen when its save goes out.
 */
export function useCounterSession(studyId: string | undefined, writes: CounterWrites) {
  const [session, setSession] = useState<CounterSession | null>(null);
  const groupRef = useRef<Group | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const writesRef = useRef(writes);
  writesRef.current = writes;

  const show = (g: Group | null) =>
    setSession(g ? { id: g.id, colorIndex: g.colorIndex, points: g.points } : null);

  // Bring the server up to the group's dots; resolves to its saved id.
  const flush = async (g: Group): Promise<string | null> => {
    if (g.creating) {
      await g.creating;
      if (g.id === null) return null; // refused: the next dot tries again
    }
    if (g.discarded) return null;
    if (g.id === null) {
      if (g.points.length === 0) return null;
      const points = g.points;
      g.creating = writesRef.current
        .create({
          study: g.study,
          seriesInstanceUID: g.series,
          frameOfReferenceUID: g.frameOfReferenceUID,
          type: 'counter',
          points,
          style: `counter:${g.colorIndex}`,
        })
        .then(
          (saved) => {
            g.id = saved?.id ?? null;
            if (g.id) g.sent = points;
            if (groupRef.current === g) show(g);
            return g.id;
          },
          () => null,
        )
        .finally(() => {
          g.creating = null;
        });
      const id = await g.creating;
      return id ? flush(g) : null; // dots added while it was being created
    }
    // An emptied counter is left as it was until the group ends, which removes it.
    if (g.points.length === 0 || (g.sent && pointsEqual(g.sent, g.points))) return g.id;
    g.sent = g.points;
    writesRef.current.update(g.id, { points: g.points });
    return g.id;
  };

  // Save a group's last dots, and remove it if it ended up empty.
  const close = async (g: Group): Promise<string | null> => {
    const id = await flush(g);
    if (id && g.points.length === 0) {
      writesRef.current.remove(id);
      return null;
    }
    return id;
  };

  const schedule = (g: Group) => {
    clearTimeout(timerRef.current);
    if (g.id === null && !g.creating) void flush(g);
    else timerRef.current = setTimeout(() => void flush(g), COUNTER_SAVE_DELAY_MS);
  };

  const begin = (g: Group) => {
    const prev = groupRef.current;
    clearTimeout(timerRef.current);
    if (prev) void close(prev);
    groupRef.current = g;
    show(g);
  };

  const change = (next: (points: AnnPoint[]) => AnnPoint[]) => {
    const g = groupRef.current;
    if (!g) return;
    g.points = next(g.points);
    show(g);
    schedule(g);
  };

  // What is still unsaved goes out when the page is left.
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (groupRef.current) void closeRef.current(groupRef.current);
    },
    [],
  );

  return {
    session,
    /** Start counting a new group on `series`, ending the current one. */
    start(colorIndex: number, series: string | null, frameOfReferenceUID: string | null) {
      if (!studyId) return;
      begin({
        id: null,
        study: studyId,
        series,
        frameOfReferenceUID,
        colorIndex,
        points: [],
        sent: null,
        creating: null,
        discarded: false,
      });
    },
    /** Go on counting a saved counter, ending the current group. */
    resume(a: Annotation) {
      // Already counting it: carry on. Its saved points lag the dots not yet
      // written, so restarting from them would drop those dots.
      if (groupRef.current?.id === a.id) return;
      begin({
        id: a.id,
        study: a.study,
        series: a.seriesInstanceUID ?? null,
        frameOfReferenceUID: a.frameOfReferenceUID ?? null,
        colorIndex: counterColorIndex(a.style),
        points: [...a.points],
        sent: a.points,
        creating: null,
        discarded: false,
      });
    },
    add(p: AnnPoint) {
      change((points) => [...points, p]);
    },
    removeAt(index: number) {
      change((points) => points.filter((_, i) => i !== index));
    },
    /**
     * Stop counting: the last dots are saved and an emptied group is removed.
     * Resolves to the saved counter's id, or null when nothing was kept.
     */
    end(): Promise<string | null> {
      const g = groupRef.current;
      if (!g) return Promise.resolve(null);
      clearTimeout(timerRef.current);
      groupRef.current = null;
      show(null);
      return close(g);
    },
    /**
     * Stop counting and write nothing more (the counter is being deleted).
     * Returns the saved id, if any, for the caller to remove; a create still
     * on its way is removed when it lands.
     */
    discard(): string | null {
      const g = groupRef.current;
      if (!g) return null;
      clearTimeout(timerRef.current);
      groupRef.current = null;
      show(null);
      g.discarded = true;
      void g.creating?.then((id) => id && writesRef.current.remove(id));
      return g.id;
    },
  };
}
